// Offline mutation queue with idempotent server sync
// Namespaced by app + authenticated user ID for shared-device safety

export interface QueuedMutation {
  id: string; // clientMutationId (UUID)
  mutationType: 'saveLearnerProgress' | 'submitReadiness';
  payload: Record<string, unknown>;
  createdAt: number;
  retries: number;
  lastError?: string;
  status: 'queued' | 'syncing' | 'error';
}

const APP_PREFIX = 'rb';
const MAX_RETRIES = 5;

function storageKey(userId: string): string {
  return `${APP_PREFIX}-mutation-queue:${userId}`;
}

function loadQueue(userId: string): QueuedMutation[] {
  try { return JSON.parse(localStorage.getItem(storageKey(userId)) || '[]'); } catch { return []; }
}

function saveQueue(userId: string, queue: QueuedMutation[]) {
  localStorage.setItem(storageKey(userId), JSON.stringify(queue));
}

export function enqueue(userId: string, mutation: Omit<QueuedMutation, 'createdAt' | 'retries' | 'status'>): QueuedMutation {
  const queue = loadQueue(userId);
  const existing = queue.find(m => m.id === mutation.id);
  if (existing) return existing;
  const entry: QueuedMutation = { ...mutation, createdAt: Date.now(), retries: 0, status: 'queued' };
  queue.push(entry);
  saveQueue(userId, queue);
  return entry;
}

export function removeFromQueue(userId: string, id: string) {
  const queue = loadQueue(userId).filter(m => m.id !== id);
  saveQueue(userId, queue);
}

export function getQueue(userId: string): QueuedMutation[] {
  return loadQueue(userId);
}

export function updateMutationStatus(userId: string, id: string, status: QueuedMutation['status'], error?: string) {
  const queue = loadQueue(userId);
  const m = queue.find(q => q.id === id);
  if (m) {
    m.status = status;
    if (error) m.lastError = error;
    if (status === 'error') m.retries++;
    saveQueue(userId, queue);
  }
}

export function getPendingCount(userId: string): number {
  return loadQueue(userId).filter(m => m.status !== 'error' || m.retries < MAX_RETRIES).length;
}

export function getQueueStatus(userId: string): 'idle' | 'queued' | 'syncing' | 'error' {
  const queue = loadQueue(userId);
  if (queue.length === 0) return 'idle';
  if (queue.some(m => m.status === 'syncing')) return 'syncing';
  if (queue.some(m => m.status === 'error' && m.retries >= MAX_RETRIES)) return 'error';
  return 'queued';
}

export async function drainQueue(
  userId: string,
  applyFn: (mutation: QueuedMutation) => Promise<boolean>,
): Promise<{ applied: number; failed: number }> {
  const queue = loadQueue(userId).sort((a, b) => a.createdAt - b.createdAt);
  let applied = 0;
  let failed = 0;

  for (const mutation of queue) {
    if (mutation.retries >= MAX_RETRIES) { failed++; continue; }

    updateMutationStatus(userId, mutation.id, 'syncing');

    // Exponential backoff
    if (mutation.retries > 0) {
      const delay = Math.min(1000 * Math.pow(2, mutation.retries), 30000);
      await new Promise(r => setTimeout(r, delay));
    }

    const success = await applyFn(mutation);
    if (success) {
      removeFromQueue(userId, mutation.id);
      applied++;
    } else {
      updateMutationStatus(userId, mutation.id, 'error');
      failed++;
    }
  }

  return { applied, failed };
}
